import { Action } from 'redux';
import { put, select } from '@redux-saga/core/effects';

import { CheckoutActions, CheckoutActionTypes } from '../checkout.types';
import { ACTION_CREATOR_NAME, userHelper, orderHelper, ExponeaCheckoutSteps, deliveryHelper, analyticsHelper, errorToBasketErrorModel } from 'helpers';
import { AppState, ErrorAction } from '../../root.types';
import { api } from 'repositories/api';
import { PlacedOrderModel, OrderCodeModel } from 'models';
import { BasketErrorModel, DEFAULT_ERROR_TEXT } from 'models/Errors';
import { loadPaymentSaga } from '../../payment/actions';
import { basketStorage } from 'storages';
import { BasketActionTypes } from 'store/basket';
import { saveCurrentPageSaga } from 'store/basket/actions';
import { PaymentActionTypes } from '../../payment';
import { PaymentDetailsModel } from 'models/Payment';
import { BasketDetailsModel, BasketInfoModel } from 'models/Basket';
import { BasketSagaReturnType, callBasketSaga } from 'store/sagaHelpers';

export interface InitOrderSummaryPageAction extends Action<CheckoutActionTypes.INIT_ORDER_SUMMARY_PAGE> {
    currentPage: string;
}

export interface InitOrderSummaryPageActionStart extends Action<CheckoutActionTypes.INIT_ORDER_SUMMARY_PAGE_START> {
}

export interface InitOrderSummaryPageActionSuccess extends Action<CheckoutActionTypes.INIT_ORDER_SUMMARY_PAGE_SUCCESS> {
    order: PlacedOrderModel;
}

export interface InitOrderSummaryPageActionError extends ErrorAction<CheckoutActionTypes.INIT_ORDER_SUMMARY_PAGE_ERROR> {
}

export const initOrderSummaryPage: (currentPage: string) => InitOrderSummaryPageAction = (currentPage: string) => ({
    type: CheckoutActionTypes.INIT_ORDER_SUMMARY_PAGE,
    currentPage
});
initOrderSummaryPage[ACTION_CREATOR_NAME] = 'initOrderSummaryPage';

export type InitOrderSummaryPageFunc = (...args: Parameters<typeof initOrderSummaryPage>) => void;

const USER_DETAILS_NOT_FOUND_ERROR_MESSAGE = 'Sorry there has been an issue, seems like we can not find your user details, please try and login in again';
const USER_NOT_FOUND_ERROR_MESSAGE = 'No User found';
const DEFAULT_ERROR_MESSAGE = 'Was unable to find an order';
const ACCESS_DENIED_ERROR_MESSAGE = 'Access is denied';
const FINANCE_APPLICATION_ERROR_MESSAGE = 'Sorry we were unable to find your finance application';

export function* initOrderSummaryPageSaga({ currentPage }: InitOrderSummaryPageAction): BasketSagaReturnType {
    yield put<CheckoutActions>({
        type: CheckoutActionTypes.INIT_ORDER_SUMMARY_PAGE_START
    });

    try {
        // save current page is called twice according to the old system
        yield callBasketSaga(saveCurrentPageSaga, {
            type: BasketActionTypes.SAVE_CURRENT_PAGE,
            currentPage
        });

        const user = userHelper.getUser();
        userHelper.removeSessionId();

        if (!user) {
            throw new BasketErrorModel(USER_DETAILS_NOT_FOUND_ERROR_MESSAGE, undefined, 'initOrderSummaryPageSaga');
        }

        const username = user.username!;

        const lastOrderCode: OrderCodeModel = yield orderHelper.getLastOrderCode(username, true);
        if (lastOrderCode === null) {
            throw new BasketErrorModel(DEFAULT_ERROR_TEXT, 'Last order code has not found', 'initOrderSummaryPageSaga');
        }

        const order: PlacedOrderModel | null = yield api.hybris.getOrder(username, lastOrderCode.orderCode);

        if (!order) {
            throw new BasketErrorModel(DEFAULT_ERROR_TEXT, `Order by code ${lastOrderCode.orderCode} has not found`, 'initOrderSummaryPageSaga');
        }

        yield createBasket(username, order);

        yield restorePaymentDetailsForLastOrder(username, lastOrderCode.basketId);

        const state: AppState = yield select();

        const phone = state.checkoutState?.deliveryDetails?.phone;

        sendAnalytics(order, state.paymentState.paymentDetails, phone);

        yield callBasketSaga(saveCurrentPageSaga, {
            type: BasketActionTypes.SAVE_CURRENT_PAGE,
            currentPage
        });
        yield put<CheckoutActions>({
            type: CheckoutActionTypes.INIT_ORDER_SUMMARY_PAGE_SUCCESS,
            order
        });

        return true;
    } catch (error) {
        yield put<CheckoutActions>({
            type: CheckoutActionTypes.INIT_ORDER_SUMMARY_PAGE_ERROR,
            error: new BasketErrorModel(
                formatErrorMessage(error.message),
                undefined,
                'initOrderSummaryPageSaga',
                undefined,
                errorToBasketErrorModel(error, 'initOrderSummaryPageSaga')
            )
        });

        return false;
    }
}

function* createBasket(username: string, lastOrder: PlacedOrderModel): Generator<any, void, any> {
    // createBasket api call creates new basket only if no basket exists for the current user
    const createdBasket: BasketInfoModel = yield api.hybris.createBasket(username);

    let basket: BasketDetailsModel = yield api.hybris.getBasketDetailsByBasketId(username, createdBasket.guid);

    if (basket.deliveryAddress === null) {
        yield deliveryHelper.calculateDeliveryCost(null, lastOrder.deliveryAddress.postcode, username, basket.guid);
    }

    basketStorage.saveBasket(createdBasket);
    userHelper.saveSessionId(basket.session.sessionId);
}

function* restorePaymentDetailsForLastOrder(username: string, basketId: string): Generator<any, void, any> {
    // for progress bar (to set correct flow)
    // for order summary panel (to retrieve deposit sum for card flow, monthly payment and months for finance flow)

    const session = yield api.hybris.getSessionByBasketId(username, basketId);
    yield callBasketSaga(loadPaymentSaga, {
        type: PaymentActionTypes.LOAD_PAYMENT,
        passedSessionId: session.sessionId
    });
}

function formatErrorMessage(originalMessage: string): string {
    if (originalMessage === ACCESS_DENIED_ERROR_MESSAGE) {
        return USER_NOT_FOUND_ERROR_MESSAGE;
    }

    if (originalMessage !== USER_NOT_FOUND_ERROR_MESSAGE && originalMessage !== USER_DETAILS_NOT_FOUND_ERROR_MESSAGE && originalMessage !== FINANCE_APPLICATION_ERROR_MESSAGE) {
        return DEFAULT_ERROR_MESSAGE;
    }

    return originalMessage;
}

function sendAnalytics(order: PlacedOrderModel, paymentDetails: PaymentDetailsModel, phone: string | null | undefined): void {
    analyticsHelper.withLogging(() => {
        api.exponea.checkoutStage(ExponeaCheckoutSteps.Summary, order, paymentDetails.paymentType);

        api.google.purchaseStep(
            order.code,
            order.entries,
            order.totalPriceWithTax?.value || 0,
            order.totalTax?.value || 0,
            order.deliveryCost?.value || 0,
            order.user!.uid,
            phone || ''
        );

        api.olapic.purchase(order.entries, order.guid);
    }, 'initOrderSummaryPageSaga');
}
